import { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Barcode from 'react-barcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Printer } from 'lucide-react';
import { Shipment } from '@/types/shipment.types';
import { formatDate, formatCurrency, formatPhone } from '@/lib/formatters';
import { PAYMENT_TYPE_LABELS } from '@/lib/constants';
import { NiimbotB1PrintButton } from './NiimbotB1PrintButton';

export type LabelType = 'standard' | 'compact' | 'thermal' | 'detailed' | 'minimal';

interface ExtendedShipment extends Shipment {
  tenant?: { name: string };
}

interface PrintLabelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shipment: ExtendedShipment;
  labelType: LabelType;
}

export const LABEL_OPTIONS: { type: LabelType; name: string; description: string }[] = [
  { type: 'standard', name: 'ملصق قياسي', description: 'ملصق A6 مع جميع التفاصيل' },
  { type: 'compact', name: 'ملصق مضغوط', description: 'ملصق صغير للطرود الصغيرة' },
  { type: 'thermal', name: 'ملصق حراري', description: 'للطابعات الحرارية 10×15 سم' },
  { type: 'detailed', name: 'ملصق تفصيلي', description: 'يحتوي على جميع المعلومات' },
  { type: 'minimal', name: 'ملصق بسيط', description: 'QR ورقم التتبع فقط' },
];

export function PrintLabelDialog({ open, onOpenChange, shipment, labelType }: PrintLabelDialogProps) {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
        <head>
          <title>ملصق الشحنة - ${shipment.tracking_number}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              direction: rtl;
              padding: 10mm;
            }
            @media print {
              body { padding: 0; }
              @page { margin: 5mm; }
            }
          </style>
        </head>
        <body>
          ${content.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>معاينة الملصق</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Preview */}
          <div className="bg-white rounded-lg border p-4 overflow-auto" dir="rtl">
            <div ref={printRef}>
              {labelType === 'standard' && <StandardLabel shipment={shipment} />}
              {labelType === 'compact' && <CompactLabel shipment={shipment} />}
              {labelType === 'thermal' && <ThermalLabel shipment={shipment} />}
              {labelType === 'detailed' && <DetailedLabel shipment={shipment} />}
              {labelType === 'minimal' && <MinimalLabel shipment={shipment} />}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              إغلاق
            </Button>
            <NiimbotB1PrintButton shipment={shipment} />
            <Button onClick={handlePrint} className="gap-2">
              <Printer className="h-4 w-4" />
              طباعة
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Standard A6 Label
export function StandardLabel({ shipment }: { shipment: ExtendedShipment }) {
  return (
    <div style={{ 
      width: '105mm', 
      minHeight: '148mm', 
      padding: '5mm',
      border: '2px solid #000',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      backgroundColor: '#fff',
      color: '#000'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4mm', borderBottom: '2px solid #000', paddingBottom: '3mm' }}>
        <div>
          <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{shipment.tenant?.name || 'شركة الشحن'}</div>
          <div style={{ fontSize: '10px', color: '#666' }}>نظام إدارة الشحنات</div>
        </div>
        <QRCodeSVG value={shipment.tracking_number} size={50} />
      </div>

      {/* Tracking Number + Linear Barcode */}
      <div style={{ textAlign: 'center', padding: '3mm', backgroundColor: '#f0f0f0', marginBottom: '3mm', border: '1px solid #ccc' }}>
        <div style={{ fontSize: '10px', color: '#666' }}>رقم التتبع</div>
        <div style={{ fontSize: '20px', fontWeight: 'bold', letterSpacing: '1px', marginBottom: '2mm' }}>{shipment.tracking_number}</div>
        <Barcode value={shipment.tracking_number} format="CODE128" height={40} width={1.4} fontSize={10} displayValue={false} margin={0} />
      </div>

      {/* Sender Section */}
      <div style={{ marginBottom: '3mm', padding: '2mm', border: '1px solid #ddd', backgroundColor: '#fafafa' }}>
        <div style={{ fontWeight: 'bold', borderBottom: '1px solid #ddd', paddingBottom: '1mm', marginBottom: '2mm' }}>المرسل</div>
        <div>{shipment.sender_info?.name || '-'}</div>
        <div style={{ fontSize: '11px' }}>{formatPhone(shipment.sender_info?.phone)}</div>
        <div style={{ fontSize: '10px', color: '#666' }}>{shipment.from_address?.city} - {shipment.from_address?.neighborhood}</div>
      </div>

      {/* Recipient Section */}
      <div style={{ marginBottom: '3mm', padding: '2mm', border: '2px solid #000', backgroundColor: '#fff' }}>
        <div style={{ fontWeight: 'bold', borderBottom: '1px solid #000', paddingBottom: '1mm', marginBottom: '2mm' }}>المستلم</div>
        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{shipment.recipient_info?.name || '-'}</div>
        <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{formatPhone(shipment.recipient_info?.phone)}</div>
        <div style={{ fontSize: '11px' }}>{shipment.to_address?.street}</div>
        <div style={{ fontSize: '11px', fontWeight: 'bold' }}>{shipment.to_address?.city} - {shipment.to_address?.neighborhood}</div>
      </div>

      {/* Shipment Details */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2mm', marginBottom: '3mm', fontSize: '11px' }}>
        <div style={{ padding: '2mm', border: '1px solid #ddd' }}>
          <span style={{ color: '#666' }}>القطع: </span>
          <span style={{ fontWeight: 'bold' }}>{shipment.pieces_count || shipment.items?.length || 1}</span>
        </div>
        <div style={{ padding: '2mm', border: '1px solid #ddd' }}>
          <span style={{ color: '#666' }}>الوزن: </span>
          <span style={{ fontWeight: 'bold' }}>{shipment.weight ? `${shipment.weight} كجم` : '-'}</span>
        </div>
      </div>

      {/* COD Amount */}
      {shipment.cod_amount > 0 && (
        <div style={{ textAlign: 'center', padding: '3mm', backgroundColor: '#000', color: '#fff', marginBottom: '3mm' }}>
          <div style={{ fontSize: '10px' }}>المبلغ المطلوب تحصيله</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{formatCurrency(shipment.cod_amount)}</div>
        </div>
      )}

      {/* Notes */}
      {shipment.notes && (
        <div style={{ fontSize: '10px', padding: '2mm', backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}>
          <span style={{ fontWeight: 'bold' }}>ملاحظات: </span>{shipment.notes}
        </div>
      )}

      {/* Footer */}
      <div style={{ marginTop: '3mm', paddingTop: '2mm', borderTop: '1px dashed #ccc', fontSize: '9px', color: '#666', textAlign: 'center' }}>
        تاريخ الإنشاء: {formatDate(shipment.created_at, 'dd/MM/yyyy HH:mm')}
      </div>
    </div>
  );
}

// Compact Label
export function CompactLabel({ shipment }: { shipment: ExtendedShipment }) {
  return (
    <div style={{ 
      width: '80mm', 
      padding: '3mm',
      border: '1px solid #000',
      fontFamily: 'Arial, sans-serif',
      fontSize: '10px',
      backgroundColor: '#fff',
      color: '#000'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2mm' }}>
        <QRCodeSVG value={shipment.tracking_number} size={35} />
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: '8px', color: '#666' }}>رقم التتبع</div>
          <div style={{ fontSize: '12px', fontWeight: 'bold' }}>{shipment.tracking_number}</div>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginBottom: '2mm' }}>
        <Barcode value={shipment.tracking_number} format="CODE128" height={30} width={1.2} fontSize={8} displayValue={false} margin={0} />
      </div>

      <div style={{ borderTop: '1px solid #000', paddingTop: '2mm', marginBottom: '2mm' }}>
        <div style={{ fontWeight: 'bold', fontSize: '11px' }}>{shipment.recipient_info?.name}</div>
        <div>{formatPhone(shipment.recipient_info?.phone)}</div>
        <div style={{ fontSize: '9px' }}>{shipment.to_address?.city} - {shipment.to_address?.neighborhood}</div>
      </div>

      {shipment.cod_amount > 0 && (
        <div style={{ textAlign: 'center', padding: '2mm', backgroundColor: '#000', color: '#fff', fontWeight: 'bold' }}>
          {formatCurrency(shipment.cod_amount)}
        </div>
      )}
    </div>
  );
}

// Thermal Label (10x15cm)
export function ThermalLabel({ shipment }: { shipment: ExtendedShipment }) {
  return (
    <div style={{ 
      width: '100mm', 
      height: '150mm',
      padding: '4mm',
      fontFamily: 'Arial, sans-serif',
      fontSize: '11px',
      backgroundColor: '#fff',
      color: '#000',
      border: '1px solid #000'
    }}>
      {/* Large QR Code centered */}
      <div style={{ textAlign: 'center', marginBottom: '4mm' }}>
        <QRCodeSVG value={shipment.tracking_number} size={80} />
      </div>

      {/* Tracking Number + Code128 (scanner gun friendly) */}
      <div style={{ textAlign: 'center', marginBottom: '4mm', padding: '3mm', borderTop: '2px solid #000', borderBottom: '2px solid #000' }}>
        <div style={{ fontSize: '24px', fontWeight: 'bold', letterSpacing: '2px', marginBottom: '2mm' }}>{shipment.tracking_number}</div>
        <Barcode value={shipment.tracking_number} format="CODE128" height={50} width={1.8} fontSize={10} displayValue={false} margin={0} />
      </div>

      {/* From */}
      <div style={{ marginBottom: '3mm', padding: '2mm', backgroundColor: '#f5f5f5' }}>
        <div style={{ fontSize: '9px', color: '#666', marginBottom: '1mm' }}>من:</div>
        <div style={{ fontWeight: 'bold' }}>{shipment.sender_info?.name}</div>
        <div>{shipment.from_address?.city}</div>
      </div>

      {/* To */}
      <div style={{ marginBottom: '3mm', padding: '3mm', border: '2px solid #000' }}>
        <div style={{ fontSize: '9px', color: '#666', marginBottom: '1mm' }}>إلى:</div>
        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{shipment.recipient_info?.name}</div>
        <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{formatPhone(shipment.recipient_info?.phone)}</div>
        <div>{shipment.to_address?.street}</div>
        <div style={{ fontWeight: 'bold' }}>{shipment.to_address?.city} - {shipment.to_address?.neighborhood}</div>
      </div>

      {/* Grid Info */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '2mm', marginBottom: '3mm', textAlign: 'center' }}>
        <div style={{ padding: '2mm', border: '1px solid #000' }}>
          <div style={{ fontSize: '8px' }}>القطع</div>
          <div style={{ fontWeight: 'bold' }}>{shipment.pieces_count || 1}</div>
        </div>
        <div style={{ padding: '2mm', border: '1px solid #000' }}>
          <div style={{ fontSize: '8px' }}>الوزن</div>
          <div style={{ fontWeight: 'bold' }}>{shipment.weight || '-'}</div>
        </div>
        <div style={{ padding: '2mm', border: '1px solid #000' }}>
          <div style={{ fontSize: '8px' }}>الدفع</div>
          <div style={{ fontWeight: 'bold', fontSize: '9px' }}>{PAYMENT_TYPE_LABELS[shipment.payment_type] || shipment.payment_type}</div>
        </div>
      </div>

      {/* COD */}
      {shipment.cod_amount > 0 && (
        <div style={{ textAlign: 'center', padding: '4mm', backgroundColor: '#000', color: '#fff' }}>
          <div style={{ fontSize: '10px' }}>المبلغ المطلوب</div>
          <div style={{ fontSize: '22px', fontWeight: 'bold' }}>{formatCurrency(shipment.cod_amount)}</div>
        </div>
      )}
    </div>
  );
}

// Detailed Label
export function DetailedLabel({ shipment }: { shipment: ExtendedShipment }) {
  return (
    <div style={{ 
      width: '148mm', 
      padding: '5mm',
      border: '1px solid #000',
      fontFamily: 'Arial, sans-serif',
      fontSize: '11px',
      backgroundColor: '#fff',
      color: '#000'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4mm', paddingBottom: '3mm', borderBottom: '2px solid #000' }}>
        <div>
          <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{shipment.tenant?.name || 'شركة الشحن'}</div>
          <div style={{ fontSize: '10px', color: '#666' }}>ملصق شحنة تفصيلي</div>
        </div>
        <QRCodeSVG value={shipment.tracking_number} size={60} />
      </div>

      {/* Tracking + Code128 */}
      <div style={{ textAlign: 'center', padding: '3mm', backgroundColor: '#f0f0f0', marginBottom: '4mm', border: '1px solid #ccc' }}>
        <div style={{ fontSize: '10px', color: '#666' }}>رقم التتبع</div>
        <div style={{ fontSize: '22px', fontWeight: 'bold', letterSpacing: '2px', marginBottom: '2mm' }}>{shipment.tracking_number}</div>
        <Barcode value={shipment.tracking_number} format="CODE128" height={45} width={1.6} fontSize={10} displayValue={false} margin={0} />
        {shipment.reference_number && (
          <div style={{ fontSize: '10px', marginTop: '2mm' }}>المرجع: {shipment.reference_number}</div>
        )}
      </div>

      {/* Two Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4mm', marginBottom: '4mm' }}>
        {/* Sender */}
        <div style={{ padding: '3mm', border: '1px solid #ddd', backgroundColor: '#fafafa' }}>
          <div style={{ fontWeight: 'bold', borderBottom: '1px solid #ddd', paddingBottom: '2mm', marginBottom: '2mm' }}>المرسل</div>
          <div style={{ marginBottom: '1mm' }}><strong>الاسم:</strong> {shipment.sender_info?.name}</div>
          <div style={{ marginBottom: '1mm' }}><strong>الهاتف:</strong> {formatPhone(shipment.sender_info?.phone)}</div>
          <div style={{ marginBottom: '1mm' }}><strong>المدينة:</strong> {shipment.from_address?.city}</div>
          <div><strong>الحي:</strong> {shipment.from_address?.neighborhood}</div>
        </div>

        {/* Recipient */}
        <div style={{ padding: '3mm', border: '2px solid #000' }}>
          <div style={{ fontWeight: 'bold', borderBottom: '1px solid #000', paddingBottom: '2mm', marginBottom: '2mm' }}>المستلم</div>
          <div style={{ marginBottom: '1mm', fontSize: '13px' }}><strong>الاسم:</strong> {shipment.recipient_info?.name}</div>
          <div style={{ marginBottom: '1mm', fontSize: '13px' }}><strong>الهاتف:</strong> {formatPhone(shipment.recipient_info?.phone)}</div>
          <div style={{ marginBottom: '1mm' }}><strong>العنوان:</strong> {shipment.to_address?.street}</div>
          <div style={{ marginBottom: '1mm' }}><strong>المدينة:</strong> {shipment.to_address?.city}</div>
          <div><strong>الحي:</strong> {shipment.to_address?.neighborhood}</div>
        </div>
      </div>

      {/* Shipment Details Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '2mm', marginBottom: '4mm', fontSize: '10px' }}>
        <div style={{ padding: '2mm', border: '1px solid #ddd', textAlign: 'center' }}>
          <div style={{ color: '#666' }}>القطع</div>
          <div style={{ fontWeight: 'bold' }}>{shipment.pieces_count || shipment.items?.length || 1}</div>
        </div>
        <div style={{ padding: '2mm', border: '1px solid #ddd', textAlign: 'center' }}>
          <div style={{ color: '#666' }}>الوزن</div>
          <div style={{ fontWeight: 'bold' }}>{shipment.weight ? `${shipment.weight} كجم` : '-'}</div>
        </div>
        <div style={{ padding: '2mm', border: '1px solid #ddd', textAlign: 'center' }}>
          <div style={{ color: '#666' }}>نوع الدفع</div>
          <div style={{ fontWeight: 'bold' }}>{PAYMENT_TYPE_LABELS[shipment.payment_type] || shipment.payment_type}</div>
        </div>
        <div style={{ padding: '2mm', border: '1px solid #ddd', textAlign: 'center' }}>
          <div style={{ color: '#666' }}>نوع الخدمة</div>
          <div style={{ fontWeight: 'bold' }}>{shipment.service_type || shipment.delivery_type || '-'}</div>
        </div>
      </div>

      {/* Items */}
      {shipment.items && shipment.items.length > 0 && (
        <div style={{ marginBottom: '4mm', padding: '2mm', border: '1px solid #ddd' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '2mm' }}>المحتويات:</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                <th style={{ padding: '2mm', border: '1px solid #ddd', textAlign: 'right' }}>المنتج</th>
                <th style={{ padding: '2mm', border: '1px solid #ddd', textAlign: 'center', width: '60px' }}>الكمية</th>
                <th style={{ padding: '2mm', border: '1px solid #ddd', textAlign: 'right' }}>SKU</th>
              </tr>
            </thead>
            <tbody>
              {shipment.items.slice(0, 5).map((item, index) => (
                <tr key={index}>
                  <td style={{ padding: '2mm', border: '1px solid #ddd' }}>{item.name}</td>
                  <td style={{ padding: '2mm', border: '1px solid #ddd', textAlign: 'center' }}>{item.quantity}</td>
                  <td style={{ padding: '2mm', border: '1px solid #ddd' }}>{item.sku || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Financial */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '2mm', marginBottom: '4mm' }}>
        <div style={{ padding: '2mm', border: '1px solid #ddd', textAlign: 'center' }}>
          <div style={{ fontSize: '9px', color: '#666' }}>قيمة البضاعة</div>
          <div style={{ fontWeight: 'bold' }}>{formatCurrency(shipment.shipment_value || 0)}</div>
        </div>
        <div style={{ padding: '2mm', border: '1px solid #ddd', textAlign: 'center' }}>
          <div style={{ fontSize: '9px', color: '#666' }}>رسوم التوصيل</div>
          <div style={{ fontWeight: 'bold' }}>{formatCurrency(shipment.delivery_fees || 0)}</div>
        </div>
        <div style={{ padding: '3mm', backgroundColor: '#000', color: '#fff', textAlign: 'center' }}>
          <div style={{ fontSize: '9px' }}>المبلغ المطلوب</div>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{formatCurrency(shipment.cod_amount || 0)}</div>
        </div>
      </div>

      {/* Notes */}
      {(shipment.notes || shipment.package_description) && (
        <div style={{ padding: '2mm', backgroundColor: '#fff3cd', border: '1px solid #ffc107', marginBottom: '3mm', fontSize: '10px' }}>
          <strong>ملاحظات:</strong> {shipment.notes || shipment.package_description}
        </div>
      )}

      {/* Footer */}
      <div style={{ paddingTop: '2mm', borderTop: '1px dashed #ccc', fontSize: '9px', color: '#666', display: 'flex', justifyContent: 'space-between' }}>
        <span>تاريخ الإنشاء: {formatDate(shipment.created_at, 'dd/MM/yyyy HH:mm')}</span>
        <span>الفرع: {shipment.origin_branch || 'الفرع الرئيسي'}</span>
      </div>
    </div>
  );
}

// Minimal Label
export function MinimalLabel({ shipment }: { shipment: ExtendedShipment }) {
  return (
    <div style={{ 
      width: '60mm', 
      padding: '4mm',
      border: '1px solid #000',
      fontFamily: 'Arial, sans-serif',
      textAlign: 'center',
      backgroundColor: '#fff',
      color: '#000'
    }}>
      <QRCodeSVG value={shipment.tracking_number} size={100} style={{ marginBottom: '3mm' }} />
      <div style={{ fontSize: '16px', fontWeight: 'bold', letterSpacing: '1px', marginBottom: '2mm' }}>{shipment.tracking_number}</div>
      <Barcode value={shipment.tracking_number} format="CODE128" height={35} width={1.3} fontSize={9} displayValue={false} margin={0} />
      <div style={{ fontSize: '10px', color: '#666', marginTop: '2mm' }}>{shipment.to_address?.city}</div>
      {shipment.cod_amount > 0 && (
        <div style={{ marginTop: '3mm', padding: '2mm', backgroundColor: '#000', color: '#fff', fontWeight: 'bold' }}>
          {formatCurrency(shipment.cod_amount)}
        </div>
      )}
    </div>
  );
}
